import React from 'react';
import { cn } from '@/lib/utils';

export type StandaloneSurfaceMeasure = 'compact' | 'reading' | 'document';
export type StandaloneSurfaceAlign = 'center' | 'start';
export type StandaloneSurfaceTone = 'app' | 'surface' | 'inverse';

/**
 * Public and authentication routes live outside AppShell, so they need one
 * explicit viewport, gutter, and reading-measure contract of their own.
 *
 * Keep page-specific cards and messages inside this frame. The frame alone
 * owns viewport sizing, the responsive 16/24/32px edge gutter, and horizontal
 * centering. This prevents loading, error, and success states from moving when
 * their content changes.
 */
export const STANDALONE_SURFACE_GUTTERS = 'p-4 sm:p-6 lg:p-8';

const STANDALONE_SURFACE_MEASURES: Record<StandaloneSurfaceMeasure, string> = {
  compact: 'max-w-sm',
  reading: 'max-w-md',
  document: 'max-w-xl',
};

const ALIGN_CLASSES: Record<StandaloneSurfaceAlign, string> = {
  center: 'place-items-center',
  start: 'items-start',
};

const TONE_CLASSES: Record<StandaloneSurfaceTone, string> = {
  app: 'bg-app text-text-primary',
  surface: 'bg-surface-base text-text-primary',
  inverse: 'bg-[var(--sidebar-bg)] text-white',
};

export interface StandaloneSurfaceProps extends React.ComponentPropsWithoutRef<'main'> {
  measure?: StandaloneSurfaceMeasure;
  align?: StandaloneSurfaceAlign;
  tone?: StandaloneSurfaceTone;
  contentClassName?: string;
}

export const StandaloneSurface = React.forwardRef<HTMLElement, StandaloneSurfaceProps>(
  (
    {
      measure = 'reading',
      align = 'center',
      tone = 'app',
      className,
      contentClassName,
      children,
      ...props
    },
    ref,
  ) => (
    <main
      ref={ref}
      data-standalone-surface
      data-standalone-align={align}
      data-standalone-measure={measure}
      className={cn(
        'grid min-h-dvh w-full overflow-x-hidden',
        STANDALONE_SURFACE_GUTTERS,
        ALIGN_CLASSES[align],
        TONE_CLASSES[tone],
        className,
      )}
      {...props}
    >
      <div
        data-standalone-content
        className={cn('mx-auto w-full', STANDALONE_SURFACE_MEASURES[measure], contentClassName)}
      >
        {children}
      </div>
    </main>
  ),
);

StandaloneSurface.displayName = 'StandaloneSurface';

export default StandaloneSurface;
